import { useEffect, useState } from 'react';
import { FormProps } from 'antd/es/form/Form';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Checkbox, CheckboxProps, Col, Form, Input, Row, Switch } from 'antd';

// services
import NotificationService from 'core/services/notification';
import { CreateCurrencyPayload, onGetCurrency, onUpdateCurrency } from 'modules/currencies/services/currencies';

// components
import BackButton from 'components/backButton/backButton';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'name[en]': string;
	'name[ar]': string;
	code: string;
	default: 1 | 0;
	active: 0 | 1;
};

function UpdateCurrencyPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [updateForm] = Form.useForm();

	useEffect(() => {
		getCurrency();
	}, []);

	const getCurrency = async () => {
		try {
			const response = await onGetCurrency(id as string);
			const currency = response.data.data;

			updateForm.setFieldsValue({ 'name[ar]': currency.name.ar });
			updateForm.setFieldsValue({ 'name[en]': currency.name.en });
			updateForm.setFieldsValue({ code: currency.code });
			updateForm.setFieldsValue({ default: currency.default });
			updateForm.setFieldsValue({ active: currency.active });

			setIsLoading(false);
		} catch (error) {
			navigate('/currencies');
			NotificationService.error(t('COMMON.OPS'), t('CURRENCIES.FETCH_ERROR'));
		}
	};

	const onDefaultChange = async (checked: boolean) => {
		updateForm.setFieldValue('default', checked ? 1 : 0);
	};

	const onStatusChange = async (checked: boolean) => {
		updateForm.setFieldValue('active', checked ? 1 : 0);
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const updateCurrencyPayload: Partial<CreateCurrencyPayload> = {
				name: {
					ar: values['name[ar]'],
					en: values['name[en]'],
				},
				code: values.code,
				default: values.default,
				active: values.active,
			};

			await onUpdateCurrency(id as string, updateCurrencyPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('CURRENCIES.UPDATED_SUCCESS'));
			navigate('/currencies');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('CURRENCIES.UPDATE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('CURRENCIES.UPDATE_TITLE')}</h3>
			</div>

			<div className="sectionContainer">
				<h5 style={{ marginBottom: 20 }}>{t('CURRENCIES.DETAILS')}</h5>

				{!isLoading && (
					<>
						<Form name="updateForm" form={updateForm} onFinish={onSubmit}>
							<Row>
								<Col span={24} md={12}>
									<Form.Item<FieldType> name="name[en]" rules={[{ required: true, message: t('CURRENCIES.VALIDATION.NAME_EN') }]}>
										<Input size="large" placeholder={t('CURRENCIES.PLACEHOLDER.NAME_EN')} />
									</Form.Item>
								</Col>
							</Row>
							<Row>
								<Col span={24} md={12}>
									<Form.Item<FieldType> name="name[ar]" rules={[{ required: true, message: t('CURRENCIES.VALIDATION.NAME_AR') }]}>
										<Input size="large" placeholder={t('CURRENCIES.PLACEHOLDER.NAME_AR')} />
									</Form.Item>
								</Col>
							</Row>
							<Row>
								<Col span={24} md={12}>
									<Form.Item<FieldType>
										name="code"
										rules={[
											{ required: true, message: t('CURRENCIES.VALIDATION.CODE') },
											{ min: 3, max: 3, message: t('CURRENCIES.VALIDATION.CODE_LENGTH') },
										]}>
										<Input size="large" placeholder={t('CURRENCIES.PLACEHOLDER.CODE')} />
									</Form.Item>
								</Col>
							</Row>

							<Row>
								<Col span={24} md={12}>
									<div className="d-flex gap-2 my-3">
										<Form.Item<FieldType> name="default" hidden>
											<Input />
										</Form.Item>
										<Switch defaultChecked={updateForm.getFieldValue('default')} onChange={onDefaultChange} />
										<p className="mb-0 fw-medium">{t('CURRENCIES.DEFAULT')}</p>
									</div>
								</Col>
							</Row>

							<Row>
								<Col span={24} md={12}>
									<div className="d-flex gap-2 my-3">
										<Form.Item<FieldType> name="active" hidden>
											<Input />
										</Form.Item>
										<Switch defaultChecked={updateForm.getFieldValue('active')} onChange={onStatusChange} />
										<p className="mb-0 fw-medium">{t('COMMON.STATUS')}</p>
									</div>
								</Col>
							</Row>
						</Form>

						<Form.Item>
							<Button
								size="middle"
								type="primary"
								htmlType="submit"
								form="updateForm"
								loading={isSubmitting}
								style={{ marginTop: 20, display: 'flex' }}>
								{t('COMMON.UPDATE')}
							</Button>
						</Form.Item>
					</>
				)}
			</div>
		</>
	);
}

export default UpdateCurrencyPage;
